import { useId, useMemo } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import type { ServiceCode } from '@gov-nx/module/service';

export type RezaBlockingMutationKey = readonly [
	'reza-blocking',
	ServiceCode,
	string,
];

export interface UseRezaBlockingMutationResult {
	blockingMutationKey: RezaBlockingMutationKey;
	isBlocked: boolean;
}

/**
 * Per-mount scoped mutation key for "block REZA switching while POSTing".
 *
 * - The key is composed of a constant prefix, the service `code` and a per-mount
 *   `useId()` instance id, so it cannot collide across different services or
 *   different mounts of the same service (e.g. after navigating away and back
 *   while a previous submit is still pending in `MutationCache`).
 * - `isBlocked` is derived from `useIsMutating` filtered by this exact key, so
 *   the hook reacts only to mutations that explicitly opt in by registering
 *   with the same `blockingMutationKey`.
 *
 * Intended usage:
 * ```ts
 * const { blockingMutationKey, isBlocked } = useRezaBlockingMutation(code);
 * const submitMutation = usePoMutation({ mutationKey: blockingMutationKey, ... });
 * ```
 */
export const useRezaBlockingMutation = (
	code: ServiceCode
): UseRezaBlockingMutationResult => {
	const instanceId = useId();
	const blockingMutationKey = useMemo(
		() => ['reza-blocking', code, instanceId] as const,
		[code, instanceId]
	);
	const blockingMutationsCount = useIsMutating({
		mutationKey: blockingMutationKey,
	});

	return {
		blockingMutationKey,
		isBlocked: blockingMutationsCount > 0,
	};
};
